import { useAtom } from 'jotai';
import sortBy from 'just-sort-by';
import React, { FunctionComponent, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import {
  isInitializingAtom,
  refinerItemsAtom,
  selectedLocationIdAtom,
  selectedRefinerItemsAtom,
  selectedSitesAtom,
  refinersExpandedAtom
} from '../utils/AtomConfig';
import Constants from '../utils/Constants';
import { FilterFlag } from './FilterFlag';

interface IRefinerProps {
  title: string;
  property: string;
}

const useStyles = makeStyles({
  checkbox: {
    display: 'flex',
    alignItems: 'flex-start',
    '& .MuiFormControlLabel-label': {
      marginTop: 8,
    },
  },
});

export const Refiner: FunctionComponent<IRefinerProps> = (
  props: IRefinerProps
) => {
  const classes = useStyles();
  const [refinersExpanded] = useAtom(refinersExpandedAtom);
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedLocationId] = useAtom(selectedLocationIdAtom);
  const [isInitializing] = useAtom(isInitializingAtom);
  const [refinerItems] = useAtom(refinerItemsAtom);
  const [selectedRefiners, setSelectedRefiners] = useAtom(
    selectedRefinerItemsAtom
  );
  const [selectedSites] = useAtom(selectedSitesAtom);

  function updateCheckedItem(value: string, checked: boolean) {
    let updatedSelectedRefiners = [...selectedRefiners];
    if (checked) {
      // Add selected refiner to the array
      updatedSelectedRefiners.push({ property: props.property, value });
    } else {
      // remove selected refiner from the array
      updatedSelectedRefiners = updatedSelectedRefiners.filter(
        (refiner) =>
          refiner.property !== props.property || refiner.value !== value
      );
    }
    setSelectedRefiners(updatedSelectedRefiners);
  }

  function isItemChecked(property: string, value: string) {
    return selectedRefiners.some(
      (refiner) => refiner.property === property && refiner.value === value
    );
  }

  const isLocationSelected = Boolean(selectedLocationId);
  const isAnySiteSelected = Boolean(selectedSites.length);
  const hasSelectedRefiners = Boolean(
    selectedRefiners.filter((refiner) => refiner.property === props.property)
      .length
  );

  const items =
    refinerItems &&
    refinerItems[props.property] &&
    sortBy(refinerItems[props.property], (item) => item.index);

  return (
    <Accordion
      expanded={isExpanded}
      onChange={(_, expanded) => setIsExpanded(expanded)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls='panel-function-content'
        id='panel-function-header'
        {...Constants.BASE_STYLES.ACCORDION_SUMMARY}
      >
        <Typography>{props.title}</Typography>
        {hasSelectedRefiners && <FilterFlag />}
      </AccordionSummary>

      {/* Show loading spinnner if it is on initializing */}
      {isInitializing && isAnySiteSelected && (
        <AccordionDetails>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={20} />
          </Box>
        </AccordionDetails>
      )}

      {/* Show message if site is not selected */}
      {!isAnySiteSelected && (
        <AccordionDetails sx={{ textAlign: 'center' }}>
          <Typography>...</Typography>
        </AccordionDetails>
      )}

      {/* Show message if empty */}
      {isLocationSelected &&
        isAnySiteSelected &&
        !isInitializing &&
        (!items || items.length === 0) && (
          <AccordionDetails sx={{ textAlign: 'center' }}>
            <Typography>No filter options available</Typography>
          </AccordionDetails>
        )}

      {/* Show items if it's not initializing */}
      {!isInitializing && isAnySiteSelected && isLocationSelected && items && (
        <AccordionDetails {...Constants.BASE_STYLES.ACCORDION_DETAILS}>
          <FormGroup>
            {items.map((item) => (
                <FormControlLabel
                  {...Constants.BASE_STYLES.FORM_CONTROL_LABEL_PROPS}
                  key={item.index}
                  value={item.refinerValue}
                  label={item.refinerLabel}
                  className={classes.checkbox}
                  control={
                    <Checkbox
                      {...Constants.BASE_STYLES.FILTER_ITEM_PROPS}
                      checked={isItemChecked(props.property, item.refinerValue)}
                      onChange={(event, checked) =>
                        updateCheckedItem(item.refinerValue, checked)
                      }
                    />
                  }
                />
            ))}
          </FormGroup>
        </AccordionDetails>
      )}
    </Accordion>
  );
};
